import _ from 'lodash';
import { useState } from 'react';
import { getUserRoleOptions } from '@/administration/userManagement/users/user.utilities';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { SimpleSaveFormBuilder } from '@/formbuilder/SimpleSaveFormBuilder.page';
import { FormUnsavedChangesReporter } from '@/header/FormUnsavedChangesReporter.atom';
import { changedProfileProperties, trackUserProfileSave } from '@/header/userProfileTracking.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { setCurrentUser } from '@/workbench/workbench.actions';
import { sqUsersApi } from '@seeqdev/api/endpoint/UsersApi';
import { SeeqNames } from '@seeqdev/constants';
import { faLock } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import type { FormElement } from '@/formbuilder/formBuilder.constants';
import type { IdentityPreviewV1 } from '@seeqdev/api/model/IdentityPreviewV1';
import type { UserInputV1 } from '@seeqdev/api/model/UserInputV1';

type EditUserProfileProps = {
  closeFn: () => void;
  /** called after the profile or password is saved, so the modal can decide what comes next */
  onSaved: () => void;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
};

type ProfileFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  currentPassword?: string;
  newPassword: string;
  confirmPassword?: string;
  role: string;
  site: string;
  manager: IdentityPreviewV1;
};

export const EditUserProfile = ({ closeFn, onSaved, onUnsavedChangesChange }: EditUserProfileProps) => {
  const { currentUser } = useFlux(sqWorkbenchStore);

  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  const isUserSSO = !currentUser.isPasswordSettable;
  const isCurrentUserMissingInfo = sqWorkbenchStore.isCurrentUserMissingInfo();

  const hasUnsavedChanges = (values: ProfileFormValues): boolean => {
    const typedPassword =
      editingPassword && [values.currentPassword, values.newPassword, values.confirmPassword].some(Boolean);
    return typedPassword || changedProfileProperties(currentUser, values).length > 0;
  };

  const updateUser = (values: ProfileFormValues) => {
    const { firstName, lastName, email, newPassword, site, manager, role } = values;
    const name = `${_.trim(firstName)} ${_.trim(lastName)}`;

    if (editingPassword) {
      return (
        sqUsersApi
          .updatePassword({ currentPassword, newPassword }, { id: currentUser.id })
          // We assume that authProvider is Seeq because only those users can change their password.
          .then(() => {
            trackUserProfileSave('Edit Profile', ['password'], 'success');
            successToast({ messageKey: 'USER.PASSWORD_CHANGED' });
          })
          .then(() => onSaved())
          .catch((error) => {
            errorToast({ httpResponseOrError: error });
            trackUserProfileSave('Edit Profile', ['password'], 'failure', error);
          })
          .finally(() => {
            setEditingPassword(false);
          })
      );
    }

    const userProps = {
      email,
      firstName,
      lastName,
      name,
      role: role || '',
      site: site || '',
      manager: _.isEmpty(manager) ? SeeqNames.Database.Utils.EmptyGuid : manager.id,
      ...(!isUserSSO && { username: email }),
    };

    const changedProperties = changedProfileProperties(currentUser, {
      firstName,
      lastName,
      email,
      role,
      site,
      manager,
    });

    return sqUsersApi
      .updateUser(userProps as unknown as UserInputV1, { id: currentUser.id })
      .then(() => setCurrentUser())
      .then(() => {
        trackUserProfileSave('Edit Profile', changedProperties, 'success');
        successToast({ messageKey: 'USER.PROFILE_UPDATED' });
      })
      .then(() => onSaved())
      .catch((error) => {
        errorToast({ httpResponseOrError: error });
        trackUserProfileSave('Edit Profile', changedProperties, 'failure', error);
      });
  };

  const displayEditUser = () => setEditingPassword(false);

  const passwordChangeForm: FormElement = {
    component: 'FormGroup',
    name: 'editPassword',
    includeIf: editingPassword,
    components: [
      {
        component: 'TextFieldFormComponent',
        name: 'currentPassword',
        label: 'USER.CURRENT_PASSWORD',
        value: currentPassword,
        // Typescript doesn't know this, but since type === 'password' the value will be a string
        onChange: (value) => setCurrentPassword(value as string),
        size: 'sm',
        includeIf: !currentUser.isAdmin,
        type: 'password',
        testId: 'currentPassword',
      },
      {
        component: 'PasswordGroupFormComponent',
        name: 'passwordFormGroup',
        value: 'passwordFormGroup',
      },
    ],
  };

  const formDefinition: FormElement[] = [
    {
      component: 'FormGroup',
      name: 'userProfile',
      includeIf: !editingPassword,
      components: [
        {
          component: 'TextFieldFormComponent',
          name: 'firstName',
          label: 'USER.FIRST_NAME',
          value: currentUser.firstName,
          required: true,
          onChange: _.noop,
          size: 'sm',
          testId: 'firstName',
        },
        {
          component: 'TextFieldFormComponent',
          name: 'lastName',
          label: 'USER.LAST_NAME',
          value: currentUser.lastName,
          required: true,
          onChange: _.noop,
          size: 'sm',
          testId: 'lastName',
        },
        {
          component: 'TextFieldFormComponent',
          name: 'email',
          // For non-SSO users make it clear that the email field is also the username
          label: isUserSSO ? 'USER.EMAIL' : 'USER.USERNAME_AND_EMAIL',
          // For non-SSO users with no email set (such as 'agent_api_key') show the username to make it clear
          // that editing the field would change the username
          value: isUserSSO ? currentUser.email : currentUser.email || currentUser.username,
          required: true,
          onChange: _.noop,
          size: 'sm',
          testId: 'email',
        },
        {
          component: 'SelectFormComponent',
          name: 'role',
          label: 'USER.ROLE',
          value: currentUser.role ?? null,
          onChange: _.noop,
          placeholder: 'USER.ROLE_PROMPT',
          testId: 'role',
          options: getUserRoleOptions(),
          skipToFormState: true,
        },
        {
          component: 'SelectIdentityFormComponent',
          name: 'manager',
          label: 'USER.MANAGER',
          onChange: _.noop,
          extraClassNames: 'mb8',
          value: currentUser.manager?.id !== SeeqNames.Database.Utils.EmptyGuid ? currentUser.manager : undefined,
          allowGroups: false,
          idForLabel: 'identityName',
          includeAllProperties: true,
          startEditable: true,
          placeholder: 'USER.MANAGER_PROMPT',
          skipToFormState: true,
          clearIdentityWhenEmpty: true,
          skipIdentity: currentUser.id,
          validation: () => {
            return false;
          },
        },
        {
          component: 'TextFieldFormComponent',
          name: 'site',
          label: 'USER.SITE',
          value: currentUser.site,
          onChange: _.noop,
          placeholder: 'USER.SITE_PROMPT',
          size: 'sm',
          testId: 'site',
          required: false,
        },
        {
          component: 'LabelFormComponent',
          name: 'passwordReset',
          value: '',
          extraClassNames: 'text-bolder',
        },
        {
          component: 'ClickableLinkFormComponent',
          name: 'setPassword',
          includeIf: currentUser.isPasswordSettable,
          onChange: _.noop,
          value: 'USER.CHANGE_PASSWORD',
          icon: faLock,
          linkAction: () => setEditingPassword(true),
        },
      ],
    },
    passwordChangeForm,
    {
      component: 'DisplayOnlyFormElementWrapper',
      name: 'unsavedChangesReporter',
      children: (
        <FormUnsavedChangesReporter<ProfileFormValues>
          hasUnsavedChanges={hasUnsavedChanges}
          onUnsavedChangesChange={onUnsavedChangesChange}
        />
      ),
    },
  ];

  return (
    // The close and cancel buttons also get hidden for SSO users that are missing their first name,
    // last name, or email address to require the user to set those attributes
    <div data-testid="editUserProfile" className="editUserProfileShortForm">
      <SimpleSaveFormBuilder
        formDefinition={formDefinition}
        submitFn={updateUser}
        submitBtnLabel={isCurrentUserMissingInfo ? 'NEXT' : 'SAVE'}
        closeFn={editingPassword ? displayEditUser : () => closeFn()}
        hideCancel={isCurrentUserMissingInfo}
      />
    </div>
  );
};
